import React, { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/PageShell";
import LandCard from "@/components/LandCard";
import PropertyCardV2 from "@/components/PropertyCardV2";
import { api } from "@/lib/api";
import { CITIES_ALL } from "@/lib/brand";
import { Trees, Store } from "lucide-react";

const PRICE = [
    { k: "any", l: "Any Price", min: null, max: null },
    { k: "u1", l: "Under ₹1 Cr", min: null, max: 1 },
    { k: "1-5", l: "₹1 – 5 Cr", min: 1, max: 5 },
    { k: "5p", l: "₹5 Cr +", min: 5, max: null },
];
const SIZE_UNITS = ["Any Size Unit", "Sq. Ft.", "Sq. Yards", "Acres", "Cents", "Hectares"];
const sel = "border border-black/15 bg-white px-4 py-2.5 text-[12px] font-medium outline-none focus:border-[var(--ebg-gold)]";

function Shell({ hero, children }) {
    return (
        <PageShell navVariant="dark">
            <section className="bg-[#0a0d18] text-white pt-32 pb-14">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">{hero}</div>
            </section>
            <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-12">{children}</section>
        </PageShell>
    );
}

function useListing(params) {
    const [items, setItems] = useState(null);
    const key = JSON.stringify(params);
    useEffect(() => {
        setItems(null);
        api.get("/properties", { params: { ...params, limit: 200 } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    return items;
}

const Grid = ({ items, empty, render, testid }) => (
    items === null ? <div className="text-black/40 py-20 text-center">Loading…</div>
        : items.length === 0 ? <div className="border border-dashed border-black/15 py-20 text-center text-black/55" data-testid={`${testid}-empty`}>{empty}</div>
            : <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid={testid}>{items.map(render)}</div>
);

export function LandProperties() {
    const [cats, setCats] = useState([]);
    const [cat, setCat] = useState("All");
    const [city, setCity] = useState("All Cities");
    const [price, setPrice] = useState("any");
    const [unit, setUnit] = useState("Any Size Unit");
    const [minSize, setMinSize] = useState("");
    useEffect(() => { api.get("/media/meta").then((r) => setCats(r.data.land_categories)).catch(() => {}); }, []);
    const p = PRICE.find((x) => x.k === price);
    const items = useListing({ land: true, ...(cat !== "All" && { land_category: cat }), ...(city !== "All Cities" && { city }), ...(p.min != null && { min_price: p.min }), ...(p.max != null && { max_price: p.max }) });
    const shown = useMemo(() => (items === null ? null : items.filter((x) => (unit === "Any Size Unit" || x.land_size_unit === unit) && (!minSize || (x.land_size || 0) >= Number(minSize)))), [items, unit, minSize]);
    return (
        <Shell hero={<>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]"><Trees size={13}/> Land Properties</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">Land opportunities, <em className="not-italic text-[var(--ebg-gold)]">title-checked.</em></h1>
            <p className="text-white/70 mt-5 max-w-xl text-sm md:text-base">Residential • Commercial • Agricultural • Development • Investment</p>
        </>}>
            <div className="flex flex-wrap gap-2 mb-5" data-testid="land-categories">
                {["All", ...cats].map((c) => (
                    <button key={c} onClick={() => setCat(c)} data-testid={`land-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${cat === c ? "bg-black text-white" : "border border-black/15 text-black/60 hover:text-black hover:border-black"}`}>{c}</button>
                ))}
            </div>
            <div className="flex flex-wrap gap-3 mb-10">
                <select value={city} onChange={(e) => setCity(e.target.value)} className={sel} data-testid="land-filter-city">{["All Cities", ...CITIES_ALL].map((o) => <option key={o}>{o}</option>)}</select>
                <select value={price} onChange={(e) => setPrice(e.target.value)} className={sel} data-testid="land-filter-price">{PRICE.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}</select>
                <select value={unit} onChange={(e) => setUnit(e.target.value)} className={sel} data-testid="land-filter-unit">{SIZE_UNITS.map((o) => <option key={o}>{o}</option>)}</select>
                <input type="number" min="0" value={minSize} onChange={(e) => setMinSize(e.target.value)} placeholder="Min. land size" className={`${sel} w-40`} data-testid="land-filter-min-size"/>
            </div>
            <Grid items={shown} testid="land-grid" empty="New land listings coming soon." render={(x) => <LandCard key={x.id} item={x}/>}/>
        </Shell>
    );
}

export function DealerProperties() {
    const [city, setCity] = useState("All Cities");
    const [lt, setLt] = useState("all");
    const items = useListing({ dealer: true, ...(city !== "All Cities" && { city }), ...(lt !== "all" && { listing_type: lt }) });
    return (
        <Shell hero={<>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]"><Store size={13}/> Dealer Properties</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">Dealer &amp; partner listings, <em className="not-italic text-[var(--ebg-gold)]">via EBG Realty.</em></h1>
            <p className="text-white/70 mt-5 max-w-xl text-sm md:text-base">Properties listed by our dealer network and reviewed by EBG before publishing. All enquiries are handled by EBG Realty.</p>
        </>}>
            <div className="flex flex-wrap gap-3 mb-10">
                <select value={city} onChange={(e) => setCity(e.target.value)} className={sel} data-testid="dealer-filter-city">{["All Cities", ...CITIES_ALL].map((o) => <option key={o}>{o}</option>)}</select>
                <select value={lt} onChange={(e) => setLt(e.target.value)} className={sel} data-testid="dealer-filter-type"><option value="all">Buy &amp; Rent</option><option value="sale">Buy</option><option value="rent">Rent</option></select>
            </div>
            <Grid items={items} testid="dealer-grid" empty="No dealer properties are listed right now." render={(x, i) => <PropertyCardV2 key={x.id} item={x} index={i}/>}/>
        </Shell>
    );
}
